// Login/Register page component
import { useState } from "react";

interface LoginPageProps {
  onLogin: (username: string, password: string) => Promise<{ success: boolean; error?: string }>;
  onRegister: (username: string, password: string, email?: string) => Promise<{ success: boolean; error?: string; isFirstUser?: boolean; needsApproval?: boolean; message?: string }>;
}

export function LoginPage({ onLogin, onRegister }: LoginPageProps) {
  const [isRegistering, setIsRegistering] = useState(false);
  const [username, setUsername] = useState("");
  const [password, setPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [email, setEmail] = useState("");
  const [error, setError] = useState("");
  const [isLoading, setIsLoading] = useState(false);
  const [showSetupMessage, setShowSetupMessage] = useState(false);
  const [showPendingApproval, setShowPendingApproval] = useState(false);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError("");
    setIsLoading(true);

    try {
      if (isRegistering) {
        // Username validation
        if (username.length < 3) {
          setError("Username must be at least 3 characters");
          setIsLoading(false);
          return;
        }

        if (!/^[a-zA-Z0-9_]+$/.test(username)) {
          setError("Username can only contain letters, numbers, and underscores");
          setIsLoading(false);
          return;
        }

        if (password !== confirmPassword) {
          setError("Passwords do not match");
          setIsLoading(false);
          return;
        }

        if (password.length < 6) {
          setError("Password must be at least 6 characters");
          setIsLoading(false);
          return;
        }

        // Email validation if provided
        if (email && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)) {
          setError("Invalid email format");
          setIsLoading(false);
          return;
        }

        const result = await onRegister(username, password, email || undefined);

        if (result.success) {
          if (result.isFirstUser) {
            setShowSetupMessage(true);
            // Auto-login after registration
            setTimeout(async () => {
              await onLogin(username, password);
            }, 1500);
          } else if (result.needsApproval) {
            // Show pending approval message
            setShowPendingApproval(true);
          } else {
            // Fallback: switch to login
            setIsRegistering(false);
            setError("");
            setPassword("");
            setConfirmPassword("");
          }
        } else {
          setError(result.error || "Registration failed");
        }
      } else {
        const result = await onLogin(username, password);

        if (!result.success) {
          setError(result.error || "Login failed");
        }
      }
    } catch (err) {
      setError("An error occurred");
    } finally {
      setIsLoading(false);
    }
  };

  if (showSetupMessage) {
    return (
      <div style={styles.container}>
        <div style={styles.card}>
          <div style={styles.successIcon}>&#10003;</div>
          <h2 style={styles.title}>Setup Complete!</h2>
          <p style={styles.message}>
            Admin account created successfully. Logging you in...
          </p>
        </div>
      </div>
    );
  }

  if (showPendingApproval) {
    return (
      <div style={styles.container}>
        <div style={styles.card}>
          <div style={styles.pendingIcon}>&#128274;</div>
          <h2 style={styles.title}>Account Pending Approval</h2>
          <p style={styles.message}>
            Your account has been created successfully!
          </p>
          <p style={{ ...styles.message, marginTop: "12px" }}>
            An administrator will review and activate your account.
            You will be able to login once your account is approved.
          </p>
          <button
            style={{ ...styles.button, marginTop: "24px" }}
            onClick={() => {
              setShowPendingApproval(false);
              setIsRegistering(false);
              setUsername("");
              setPassword("");
              setConfirmPassword("");
              setEmail("");
            }}
          >
            Back to Login
          </button>
        </div>
      </div>
    );
  }

  return (
    <div style={styles.container}>
      <div style={styles.card}>
        <div style={styles.logo}>
          <span style={styles.logoIcon}>&#127860;</span>
          <h1 style={styles.logoText}>DishSnap</h1>
        </div>

        <h2 style={styles.title}>
          {isRegistering ? "Create Account" : "Sign In"}
        </h2>

        {!isRegistering && (
          <p style={styles.subtitle}>
            Sign in to access your dashboard
          </p>
        )}

        {isRegistering && (
          <p style={styles.subtitle}>
            Create your admin account to get started
          </p>
        )}

        <form onSubmit={handleSubmit} style={styles.form}>
          <div style={styles.inputGroup}>
            <label style={styles.label}>Username</label>
            <input
              type="text"
              value={username}
              onChange={(e) => setUsername(e.target.value)}
              style={styles.input}
              placeholder="Enter username"
              required
              autoFocus
            />
          </div>

          {isRegistering && (
            <div style={styles.inputGroup}>
              <label style={styles.label}>Email (optional)</label>
              <input
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                style={styles.input}
                placeholder="Enter email"
              />
            </div>
          )}

          <div style={styles.inputGroup}>
            <label style={styles.label}>Password</label>
            <input
              type="password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              style={styles.input}
              placeholder="Enter password"
              required
            />
          </div>

          {isRegistering && (
            <div style={styles.inputGroup}>
              <label style={styles.label}>Confirm Password</label>
              <input
                type="password"
                value={confirmPassword}
                onChange={(e) => setConfirmPassword(e.target.value)}
                style={styles.input}
                placeholder="Confirm password"
                required
              />
            </div>
          )}

          {error && (
            <div style={styles.error}>{error}</div>
          )}

          <button
            type="submit"
            style={{
              ...styles.button,
              opacity: isLoading ? 0.7 : 1,
              cursor: isLoading ? "not-allowed" : "pointer",
            }}
            disabled={isLoading}
          >
            {isLoading
              ? "Please wait..."
              : isRegistering
              ? "Create Account"
              : "Sign In"}
          </button>
        </form>

        <div style={styles.switchMode}>
          {isRegistering ? (
            <p>
              Already have an account?{" "}
              <button
                style={styles.linkButton}
                onClick={() => {
                  setIsRegistering(false);
                  setError("");
                }}
              >
                Sign In
              </button>
            </p>
          ) : (
            <p>
              First time?{" "}
              <button
                style={styles.linkButton}
                onClick={() => {
                  setIsRegistering(true);
                  setError("");
                }}
              >
                Create Account
              </button>
            </p>
          )}
        </div>
      </div>
    </div>
  );
}

const styles: Record<string, React.CSSProperties> = {
  container: {
    minHeight: "100vh",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    background: "linear-gradient(135deg, #1a1a2e 0%, #16213e 100%)",
    padding: "20px",
  },
  card: {
    background: "#1e1e2f",
    borderRadius: "16px",
    padding: "40px",
    width: "100%",
    maxWidth: "400px",
    boxShadow: "0 20px 60px rgba(0, 0, 0, 0.3)",
  },
  logo: {
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    gap: "12px",
    marginBottom: "24px",
  },
  logoIcon: {
    fontSize: "36px",
  },
  logoText: {
    margin: 0,
    fontSize: "28px",
    fontWeight: 700,
    color: "#fff",
  },
  title: {
    margin: "0 0 8px 0",
    fontSize: "24px",
    fontWeight: 600,
    color: "#fff",
    textAlign: "center" as const,
  },
  subtitle: {
    margin: "0 0 24px 0",
    fontSize: "14px",
    color: "#888",
    textAlign: "center" as const,
  },
  form: {
    display: "flex",
    flexDirection: "column" as const,
    gap: "16px",
  },
  inputGroup: {
    display: "flex",
    flexDirection: "column" as const,
    gap: "6px",
  },
  label: {
    fontSize: "13px",
    fontWeight: 500,
    color: "#aaa",
  },
  input: {
    padding: "12px 16px",
    fontSize: "15px",
    borderRadius: "8px",
    border: "1px solid #333",
    background: "#252538",
    color: "#fff",
    outline: "none",
    transition: "border-color 0.2s",
  },
  error: {
    padding: "10px 14px",
    background: "rgba(239, 68, 68, 0.1)",
    border: "1px solid rgba(239, 68, 68, 0.3)",
    borderRadius: "8px",
    color: "#ef4444",
    fontSize: "13px",
    textAlign: "center" as const,
  },
  button: {
    padding: "14px 20px",
    fontSize: "15px",
    fontWeight: 600,
    borderRadius: "8px",
    border: "none",
    background: "linear-gradient(135deg, #e60023 0%, #c7001f 100%)",
    color: "#fff",
    cursor: "pointer",
    transition: "transform 0.2s, box-shadow 0.2s",
    marginTop: "8px",
  },
  switchMode: {
    marginTop: "24px",
    textAlign: "center" as const,
    fontSize: "14px",
    color: "#888",
  },
  linkButton: {
    background: "none",
    border: "none",
    color: "#e60023",
    cursor: "pointer",
    fontWeight: 500,
    fontSize: "14px",
    padding: 0,
    textDecoration: "underline",
  },
  successIcon: {
    fontSize: "48px",
    color: "#22c55e",
    textAlign: "center" as const,
    marginBottom: "16px",
  },
  pendingIcon: {
    fontSize: "48px",
    color: "#f59e0b",
    textAlign: "center" as const,
    marginBottom: "16px",
  },
  message: {
    textAlign: "center" as const,
    color: "#888",
    fontSize: "14px",
    margin: 0,
  },
};
